import { Box, CircularProgress, GridLegacy as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { EditStateActions } from '~/components/EditStateActions';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  EditCustomDataFields,
  ReadCustomDataFields,
} from '~/legacy-components/CustomDataFields';

import { useCustomData } from './useCustomData';
import { useSxStyles } from './useStyles';

export const CustomDataTab = () => {
  const { t } = useTranslation();
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const {
    isLoading,
    readFieldData,
    editFieldData,
    control,
    handleFormSubmit,
    canEdit,
    reset,
    editMode,
    setEditMode,
  } = useCustomData({
    employeeId: selectedEmployeeId,
  });

  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const isManager = hasRoleAccessOverEmployee([Roles.MANAGER]);
  const isHrAdmin = hasRoleAccessOverEmployee([Roles.HR_ADMIN]);

  const evaluateDisableField = (editableByEmp: boolean): boolean => {
    if (isHrAdmin) {
      return false;
    } else if (isManager) {
      return editableByEmp;
    } else {
      return !editableByEmp;
    }
  };

  const { contentPadding, spinner } = useSxStyles();

  return (
    <Box sx={contentPadding}>
      <SolaForceHeading heading={t('laCustomData')}>
        <EditStateActions
          canEdit={canEdit && editFieldData.length > 0}
          readMode={!editMode}
          onEdit={() => setEditMode(true)}
          onSave={async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await handleFormSubmit();
          }}
          onCancel={() => {
            setEditMode(false);
            reset();
          }}
        />
      </SolaForceHeading>
      {isLoading ? (
        <Box sx={spinner}>
          <CircularProgress size={25} />
        </Box>
      ) : (
        <form>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={1} alignItems="center">
              {editMode
                ? editFieldData.length
                  ? editFieldData.map((f, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <EditCustomDataFields
                          control={control}
                          key={`${index}/${f.name}`}
                          {...{
                            ...f,
                            inputProps: {
                              ...f.inputProps,
                              disabled: evaluateDisableField(!!f.editableByEmp),
                            },
                          }}
                        />
                      </Grid>
                    ))
                  : null
                : readFieldData.length
                ? readFieldData.map((f, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <ReadCustomDataFields key={`${index}/${f.name}`} {...f} />
                    </Grid>
                  ))
                : null}
            </Grid>
          </Box>
        </form>
      )}
    </Box>
  );
};
